import {put, call, all} from 'redux-saga/effects';
import Expense from "../../entities/expense";
import * as Actions from "../actions/expenseActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ExpenseAction = {
    expenseId: string;
    expense: Expense;
    expenses: Expense[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneExpense(action: ExpenseAction) {
	const session = tabSession.captureGeneration();
	try {
		const expense: Expense = yield call(api.findOne, 'expense', action.expenseId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.EXPENSE_FETCH_ONE_SUCCESS, expenseId: action.expenseId, expense, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EXPENSE_FETCH_ONE_FAILURE, expenseId: action.expenseId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ExpenseAction} action
 */
export function* saveExpense(action: ExpenseAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ EXPENSE_SAVE saga', action.expense?.id);

		if (action.expense === undefined || action.expense === null) {
			console.error('saveExpense received a null');
			throw new Error('saveExpense received a null');
		}

		if (!(action.expense instanceof Expense)) {
			action.expense = new Expense(action.expense);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.expense.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.expense);
			const addResult = yield call(idb.add, 'expense', action.expense);
			if (addResult.isErr()) {
				console.error(`⮂ saveExpense: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertExpenseSave(action.expense, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.EXPENSE_SAVE_FAILURE, expense: action.expense, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.EXPENSE_SAVE, expense: action.expense, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertExpenseSave(action.expense, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.EXPENSE_SAVE_FAILURE, expense: action.expense, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ExpenseAction} action
 * @param {Expense[]} action.expenses
 */
export function* saveManyExpenses(action: ExpenseAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ EXPENSE_SAVE_MANY saga', action.expenses.map(expense => expense.id).join(','));

        if (!Array.isArray(action.expenses)) {
            console.error('saveManyExpenses expected an array, received', action.expenses);
            throw new Error('saveManyExpenses expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveExpense), under these queue keys
		const dispatchedAddedOns: number[] = action.expenses.map(expense => expense?.addedOn);
		const revertAll = () => action.expenses.map((expense, i) =>
			put(Actions.revertExpenseSave(expense, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Expense[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.expenses.length; i++) {
				const expense = action.expenses[i];
				expense.persisted = false;
				ensureOperationId(expense);
				toSave.push(expense);
			}

			const addResult = yield call(idb.add, 'expense', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyExpenses: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.EXPENSE_SAVE_MANY_FAILURE, expenses: action.expenses, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.EXPENSE_SAVE_MANY, expenses: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.EXPENSE_SAVE_MANY_FAILURE, expenses: action.expenses, status: 500, err});

			console.error(`Error saving ${action.expenses.length} expenses to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ExpenseAction} action
 */
export function* removeExpense(action: ExpenseAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.expense === undefined || action.expense === null || !(action.expense instanceof Expense)) {
			console.error('removeExpense expected type Expense, received', action.expense);
			reportQueued(false);
			throw new Error('removeExpense expected type Expense');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.expense.status = 'D';
		action.expense.addedOn = newHRTimestamp();
		action.expense.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.expense);
		const addResult = yield call(idb.add, 'expense', action.expense);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeExpense: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.EXPENSE_REMOVE_FAILURE, expense: action.expense, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EXPENSE_REMOVE, expense: action.expense});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EXPENSE_REMOVE_FAILURE, expense: action.expense, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Expense.
	*
	* @param {ExpenseAction} action
	*/
export function* restoreExpense(action: ExpenseAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreExpense expected getCurrentUserId() to return a value');
        return;
    }

	if (action.expense === undefined || action.expense === null) {
        console.error('restoreExpense expected a Expense, received null');
        throw new Error('restoreExpense expected a Expense, received null');
    }
    else if (!(action.expense instanceof Expense)) action.expense = new Expense(action.expense); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.expense.status = 'U';
		action.expense.addedOn = newHRTimestamp();
		action.expense.addedBy = userId;
		action.expense.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.expense);
		const addResult = yield call(idb.add, 'expense', action.expense);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreExpense: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.EXPENSE_RESTORE_FAILURE, expense: action.expense, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EXPENSE_RESTORE, expense: action.expense});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EXPENSE_RESTORE_FAILURE, expense: action.expense, status: 500, err});

		console.error('Error restoring from database', err);
	}
}